/* eslint-disable @typescript-eslint/no-explicit-any */
// Globalny stan aplikacji - produkty
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { IWiseTableAdvancedFilter } from 'components/wise/types';

// typ komponentu
interface IComponent {
  [componentSymbol: string]: any;
}

// typ dynamicPage
interface IDynamicPage {
  [dynamicPageSymbol: string]: IComponent;
}

/** filters[pageSymbol][componentSymbol] = IWiseTableAdvancedFilter[] */
interface IWiseTableAdvancedFiltersState {
  [pageSymbol: string]: {
    [componentSymbol: string]: IWiseTableAdvancedFilter[];
  };
}

/** Bieżąca widoczność kolumn [pageSymbol][componentSymbol] = Record<columnId, boolean> */
interface IWiseTableColumnVisibilityState {
  [pageSymbol: string]: {
    [componentSymbol: string]: Record<string, boolean>;
  };
}

/** ID wybranego presetu ustawień (Zapisane ustawienia) [pageSymbol][componentSymbol] = string | null */
interface IWiseTableSelectedSettingsPresetIdState {
  [pageSymbol: string]: {
    [componentSymbol: string]: string | null;
  };
}

/** Kolejność kolumn (field_symbol) [pageSymbol][componentSymbol] = string[] */
interface IWiseTableColumnOrderState {
  [pageSymbol: string]: {
    [componentSymbol: string]: string[];
  };
}

//typ dynamicPageSlice
interface IDynamicPageSlice {
  contextData: IDynamicPage;
  contextFilters: any;
  wiseTableAdvancedFilters: IWiseTableAdvancedFiltersState;
  wiseTableColumnVisibility: IWiseTableColumnVisibilityState;
  wiseTableColumnOrder: IWiseTableColumnOrderState;
  wiseTableSelectedSettingsPresetId: IWiseTableSelectedSettingsPresetIdState;
}

// TODO: tymczasowa persystencja w localStorage (BE `/api/ui/sales-crm/filters` nie istnieje).
// Trzymamy widocznosc/kolejnosc/aktywny preset tabel, zeby przezylo F5. Docelowo usunac po
// dopisaniu BE i przelaczeniu use{Get,Post,Put}SalesCrmFilters z powrotem na HTTP.
const STORAGE_KEY = 'dynamicPageWiseTableState';

interface IPersistedDynamicPageSlice {
  wiseTableColumnVisibility: IDynamicPageSlice['wiseTableColumnVisibility'];
  wiseTableColumnOrder: IDynamicPageSlice['wiseTableColumnOrder'];
  wiseTableSelectedSettingsPresetId: IDynamicPageSlice['wiseTableSelectedSettingsPresetId'];
}

const readPersistedDynamicPageState = (): IPersistedDynamicPageSlice => {
  const empty: IPersistedDynamicPageSlice = {
    wiseTableColumnVisibility: {},
    wiseTableColumnOrder: {},
    wiseTableSelectedSettingsPresetId: {}
  };
  if (typeof window === 'undefined') return empty;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return empty;
    const parsed = JSON.parse(raw) as Partial<IPersistedDynamicPageSlice>;
    return {
      wiseTableColumnVisibility: parsed.wiseTableColumnVisibility ?? {},
      wiseTableColumnOrder: parsed.wiseTableColumnOrder ?? {},
      wiseTableSelectedSettingsPresetId: parsed.wiseTableSelectedSettingsPresetId ?? {}
    };
  } catch {
    return empty;
  }
};

const persistDynamicPageState = (state: IDynamicPageSlice): void => {
  if (typeof window === 'undefined') return;
  try {
    const snapshot: IPersistedDynamicPageSlice = {
      wiseTableColumnVisibility: state.wiseTableColumnVisibility,
      wiseTableColumnOrder: state.wiseTableColumnOrder,
      wiseTableSelectedSettingsPresetId: state.wiseTableSelectedSettingsPresetId
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // ignore quota / privacy mode
  }
};

const persistedDynamicPage = readPersistedDynamicPageState();

// dane inicjalizacyjne
const initialState: IDynamicPageSlice = {
  contextData: {},
  contextFilters: {},
  wiseTableAdvancedFilters: {},
  wiseTableColumnVisibility: persistedDynamicPage.wiseTableColumnVisibility,
  wiseTableColumnOrder: persistedDynamicPage.wiseTableColumnOrder,
  wiseTableSelectedSettingsPresetId: persistedDynamicPage.wiseTableSelectedSettingsPresetId
};

export const dynamicPageSlice = createSlice({
  name: 'dynamicPage',
  initialState,
  reducers: {
    setContextData: (state, action: PayloadAction<IDynamicPageSlice['contextData']>) => {
      const pageSymbol = Object.keys(action.payload)?.[0];
      const componentSymbol = Object.keys(action.payload?.[pageSymbol])?.[0];
      state.contextData = {
        ...state.contextData,
        [pageSymbol]: {
          ...state.contextData[pageSymbol],
          [componentSymbol]: action.payload?.[pageSymbol]?.[componentSymbol]
        }
      };
    },

    setContextFilters: (state, action: PayloadAction<IDynamicPageSlice['contextFilters']>) => {
      state.contextFilters = {
        ...state.contextFilters,
        ...action.payload
      };
    },

    setFieldValue: (state, action: any) => {
      const { pageSymbol, componentSymbol, fieldName, fieldValue } = action.payload;

      if (pageSymbol && componentSymbol && fieldName) {
        state.contextData[pageSymbol][componentSymbol][fieldName] = fieldValue;
      }
    },

    addWiseTableAdvancedFilter: (
      state,
      action: PayloadAction<{
        pageSymbol: string;
        componentSymbol: string;
        filter: IWiseTableAdvancedFilter;
      }>
    ) => {
      const { pageSymbol, componentSymbol, filter } = action.payload;
      if (!pageSymbol || !componentSymbol) return;
      if (!state.wiseTableAdvancedFilters[pageSymbol]) {
        state.wiseTableAdvancedFilters[pageSymbol] = {};
      }
      if (!Array.isArray(state.wiseTableAdvancedFilters[pageSymbol][componentSymbol])) {
        state.wiseTableAdvancedFilters[pageSymbol][componentSymbol] = [];
      }
      state.wiseTableAdvancedFilters[pageSymbol][componentSymbol].push(filter);
    },

    setWiseTableAdvancedFilters: (
      state,
      action: PayloadAction<{
        pageSymbol: string;
        componentSymbol: string;
        filters: IWiseTableAdvancedFilter[];
      }>
    ) => {
      const { pageSymbol, componentSymbol, filters } = action.payload;
      if (!pageSymbol || !componentSymbol) return;
      if (!state.wiseTableAdvancedFilters[pageSymbol]) {
        state.wiseTableAdvancedFilters[pageSymbol] = {};
      }
      state.wiseTableAdvancedFilters[pageSymbol][componentSymbol] = filters;
    },

    setWiseTableColumnVisibility: (
      state,
      action: PayloadAction<{
        pageSymbol: string;
        componentSymbol: string;
        columnVisibility: Record<string, boolean>;
      }>
    ) => {
      const { pageSymbol, componentSymbol, columnVisibility } = action.payload;
      if (!pageSymbol || !componentSymbol) return;
      if (!state.wiseTableColumnVisibility[pageSymbol]) {
        state.wiseTableColumnVisibility[pageSymbol] = {};
      }
      state.wiseTableColumnVisibility[pageSymbol][componentSymbol] = columnVisibility;
      persistDynamicPageState(state);
    },

    setWiseTableColumnOrder: (
      state,
      action: PayloadAction<{
        pageSymbol: string;
        componentSymbol: string;
        columnOrder: string[];
      }>
    ) => {
      const { pageSymbol, componentSymbol, columnOrder } = action.payload;
      if (!pageSymbol || !componentSymbol) return;
      if (!state.wiseTableColumnOrder[pageSymbol]) {
        state.wiseTableColumnOrder[pageSymbol] = {};
      }
      state.wiseTableColumnOrder[pageSymbol][componentSymbol] = columnOrder;
      persistDynamicPageState(state);
    },

    setWiseTableSelectedSettingsPresetId: (
      state,
      action: PayloadAction<{
        pageSymbol: string;
        componentSymbol: string;
        presetId: string | null;
      }>
    ) => {
      const { pageSymbol, componentSymbol, presetId } = action.payload;
      if (!pageSymbol || !componentSymbol) return;
      if (!state.wiseTableSelectedSettingsPresetId[pageSymbol]) {
        state.wiseTableSelectedSettingsPresetId[pageSymbol] = {};
      }
      state.wiseTableSelectedSettingsPresetId[pageSymbol][componentSymbol] = presetId;
      persistDynamicPageState(state);
    }
  }
});

export const dynamicPageSliceActions = dynamicPageSlice.actions;

export default dynamicPageSlice.reducer;
